import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { 
  Truck, Link2, CheckCircle, XCircle, RefreshCw, 
  Settings, Key, Globe, Webhook, Eye, EyeOff, Copy,
  ArrowRight, Zap
} from 'lucide-react';
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function FlottoIntegration() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);
  
  const [settings, setSettings] = useState({
    enabled: false,
    flotto_url: '',
    api_key: '',
    webhook_secret: '',
    auto_send_deliveries: true,
    sync_driver_status: true,
    show_live_tracking: true
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/integration/settings?tenant_id=default`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/integration/settings?tenant_id=default`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });
      
      if (response.ok) {
        toast.success('Einstellungen gespeichert!');
      } else {
        throw new Error('Speichern fehlgeschlagen');
      }
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    setConnectionStatus(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/integration/test-connection?tenant_id=default`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      setConnectionStatus(data);
      
      if (data.success) {
        toast.success('Verbindung erfolgreich!');
      } else {
        toast.error(data.error || 'Verbindung fehlgeschlagen');
      }
    } catch (error) {
      setConnectionStatus({ success: false, error: 'Netzwerkfehler' });
      toast.error('Verbindungstest fehlgeschlagen');
    } finally {
      setTesting(false);
    }
  };

  const generateWebhookSecret = () => {
    const secret = 'whsec_' + Array.from(crypto.getRandomValues(new Uint8Array(24)))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
    setSettings(prev => ({ ...prev, webhook_secret: secret }));
    toast.success('Webhook Secret generiert');
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} kopiert!`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-500 rounded-xl flex items-center justify-center">
              <Truck className="w-7 h-7 text-white" />
            </div>
            flotto.app Integration
          </h1>
          <p className="text-gray-500 mt-1">
            Verbinde Cash-X mit flotto.app für Lieferfahrer-Management
          </p>
        </div>
        
        <Badge 
          variant={settings.enabled ? "default" : "secondary"}
          className={settings.enabled ? "bg-green-100 text-green-800" : ""}
        >
          {settings.enabled ? (
            <><CheckCircle className="w-3 h-3 mr-1" /> Aktiviert</>
          ) : (
            <><XCircle className="w-3 h-3 mr-1" /> Deaktiviert</>
          )}
        </Badge>
      </div>

      {/* Info Card */}
      <Card className="bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
        <CardContent className="py-4">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Zap className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-purple-900">So funktioniert die Integration</h3>
              <div className="mt-2 flex items-center gap-2 text-sm text-purple-700">
                <span className="bg-white px-2 py-1 rounded">Lieferung erstellen</span>
                <ArrowRight className="w-4 h-4" />
                <span className="bg-white px-2 py-1 rounded">QR auf Bon</span>
                <ArrowRight className="w-4 h-4" />
                <span className="bg-white px-2 py-1 rounded">Fahrer scannt</span>
                <ArrowRight className="w-4 h-4" />
                <span className="bg-white px-2 py-1 rounded">Status sync</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Settings */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Connection Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-blue-600" />
              Verbindung
            </CardTitle>
            <CardDescription>
              Konfiguriere die Verbindung zu flotto.app
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Enable Toggle */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <Label className="font-medium">Integration aktivieren</Label>
                <p className="text-sm text-gray-500">Lieferungen werden automatisch gesendet</p>
              </div>
              <Switch
                checked={settings.enabled}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, enabled: checked }))}
                data-testid="enable-integration-switch"
              />
            </div>

            {/* flotto.app URL */}
            <div className="space-y-2">
              <Label htmlFor="flotto-url">flotto.app URL</Label>
              <Input
                id="flotto-url"
                placeholder="https://posfix-1.preview.emergentagent.com"
                value={settings.flotto_url}
                onChange={(e) => setSettings(prev => ({ ...prev, flotto_url: e.target.value }))}
                data-testid="flotto-url-input"
              />
            </div>

            {/* API Key */}
            <div className="space-y-2">
              <Label htmlFor="api-key">API Key</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    id="api-key"
                    type={showApiKey ? "text" : "password"}
                    placeholder="flotto_abc123..."
                    value={settings.api_key}
                    onChange={(e) => setSettings(prev => ({ ...prev, api_key: e.target.value }))}
                    data-testid="api-key-input"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                    onClick={() => setShowApiKey(!showApiKey)}
                  >
                    {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => copyToClipboard(settings.api_key, 'API Key')}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Test Connection Button */}
            <Button
              onClick={testConnection}
              disabled={testing || !settings.flotto_url}
              className="w-full"
              variant={connectionStatus?.success ? "default" : "outline"}
              data-testid="test-connection-btn"
            >
              {testing ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Teste...</>
              ) : connectionStatus?.success ? (
                <><CheckCircle className="w-4 h-4 mr-2 text-green-500" /> Verbunden!</>
              ) : (
                <><Link2 className="w-4 h-4 mr-2" /> Verbindung testen</>
              )}
            </Button>

            {connectionStatus && !connectionStatus.success && (
              <p className="text-sm text-red-600 bg-red-50 p-2 rounded">
                {connectionStatus.error}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Webhook Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Webhook className="w-5 h-5 text-orange-600" />
              Webhooks
            </CardTitle>
            <CardDescription>
              Sichere Kommunikation zwischen den Systemen
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Webhook Secret */}
            <div className="space-y-2">
              <Label htmlFor="webhook-secret">Webhook Secret</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    id="webhook-secret"
                    type={showWebhookSecret ? "text" : "password"}
                    placeholder="whsec_..."
                    value={settings.webhook_secret}
                    onChange={(e) => setSettings(prev => ({ ...prev, webhook_secret: e.target.value }))}
                    data-testid="webhook-secret-input"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                    onClick={() => setShowWebhookSecret(!showWebhookSecret)}
                  >
                    {showWebhookSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={generateWebhookSecret}
                >
                  Generieren
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                Dieses Secret muss auch in flotto.app konfiguriert werden
              </p>
            </div>

            <Separator />

            {/* Webhook URL Info */}
            <div className="space-y-2">
              <Label>Webhook URL (für flotto.app)</Label>
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={`${API_BASE}/api/integration/webhooks/flotto`}
                  className="bg-gray-50 text-sm"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => copyToClipboard(`${API_BASE}/api/integration/webhooks/flotto`, 'Webhook URL')}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                Trage diese URL in flotto.app ein
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Feature Toggles */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-gray-600" />
            Funktionen
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <Label className="font-medium">Auto-Senden</Label>
                <p className="text-sm text-gray-500">Lieferungen automatisch senden</p>
              </div>
              <Switch
                checked={settings.auto_send_deliveries}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, auto_send_deliveries: checked }))}
              />
            </div>
            
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <Label className="font-medium">Fahrer-Status</Label>
                <p className="text-sm text-gray-500">Status synchronisieren</p>
              </div>
              <Switch
                checked={settings.sync_driver_status}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, sync_driver_status: checked }))}
              />
            </div>
            
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <Label className="font-medium">Live-Tracking</Label>
                <p className="text-sm text-gray-500">Fahrer-Position anzeigen</p>
              </div>
              <Switch
                checked={settings.show_live_tracking}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, show_live_tracking: checked }))}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={loadSettings}>
          Zurücksetzen
        </Button>
        <Button 
          onClick={saveSettings} 
          disabled={saving}
          className="bg-purple-600 hover:bg-purple-700"
          data-testid="save-settings-btn"
        >
          {saving ? (
            <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Speichern...</>
          ) : (
            'Einstellungen speichern'
          )}
        </Button>
      </div>
    </div>
  );
}
